import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  ButtonGroup
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import apiSpec from './api_spec.json';
import { API_KEY, DEFAULT_HEADERS } from '../config/api.config';

// Define public endpoints that don't need API key
const PUBLIC_ENDPOINTS = [
  '/health',
  '/api/v1/auth/google/login',
  '/api/v1/auth/verify',
  '/api/v1/admin/domains/resolve-public',
  '/api/v1/license/validate',
  '/api/v1/license/info'
];

const ApiTestPage = () => {
  // Server configuration
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.scos.ai';
  const [server, setServer] = useState(API_BASE_URL);
  const [apiVersion, setApiVersion] = useState('/api/v1');
  const [tenantAlias, setTenantAlias] = useState('nilecom_363537');
  
  // API route selection
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [endpoints, setEndpoints] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('GET');
  
  // Path parameters
  const [pathParams, setPathParams] = useState({});
  
  // Query parameters
  const [queryParams, setQueryParams] = useState({});
  
  // Request body
  const [requestBody, setRequestBody] = useState('');
  const [showRequestBody, setShowRequestBody] = useState(false);
  
  // Response
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add this near the top with other state variables
  const [hasToken, setHasToken] = useState(false);

  // Add this near the state declarations
  const [authScheme, setAuthScheme] = useState('api_key');

  // Add new state for API documentation
  const [openApiJson, setOpenApiJson] = useState(null);
  const [openApiLoading, setOpenApiLoading] = useState(false);
  const [openApiError, setOpenApiError] = useState(null);

  // Load API routes from the OpenAPI spec
  useEffect(() => {
    try {
      // Extract unique route prefixes (e.g., /api/v1/config, /api/v1/master-data)
      const pathsObj = apiSpec.paths || {};
      const paths = Object.keys(pathsObj);
      
      const routePrefixes = new Set();
      paths.forEach(path => {
        if (path.startsWith('/api/v1/')) {
          const parts = path.split('/');
          if (parts.length >= 4) {
            routePrefixes.add(parts[3]); // Get the route category (config, master-data, etc.)
          }
        }
      });
      
      setRoutes(Array.from(routePrefixes).sort());
    } catch (err) {
      console.error('Error parsing OpenAPI spec:', err);
      setError('Failed to load API specification');
    }
  }, []);

  // Update endpoints when route changes
  useEffect(() => {
    if (!selectedRoute) {
      setEndpoints([]);
      return;
    }

    try {
      const pathsObj = apiSpec.paths || {};
      const paths = Object.keys(pathsObj);
      
      // Filter paths that match the selected route
      const routePrefix = `/api/v1/${selectedRoute}`;
      const matchingPaths = paths.filter(path => path.startsWith(routePrefix));
      
      setEndpoints(matchingPaths.sort());
      if (matchingPaths.length > 0) {
        setSelectedEndpoint(matchingPaths[0]);
      }
    } catch (err) {
      console.error('Error updating endpoints:', err);
    }
  }, [selectedRoute]);

  // Update available methods and parameters when endpoint changes
  useEffect(() => {
    if (!selectedEndpoint) {
      setSelectedMethod('GET');
      setPathParams({});
      setQueryParams({});
      setShowRequestBody(false);
      return;
    }

    try {
      const endpointObj = apiSpec.paths[selectedEndpoint] || {};
      const methods = Object.keys(endpointObj).map(m => m.toUpperCase());
      
      // Default to GET if available, otherwise use the first available method
      if (methods.includes('GET')) {
        setSelectedMethod('GET');
      } else if (methods.length > 0) {
        setSelectedMethod(methods[0]);
      }

      // Extract path parameters
      const pathParamNames = (selectedEndpoint.match(/{([^}]+)}/g) || [])
        .map(param => param.slice(1, -1));
      
      const newPathParams = {};
      pathParamNames.forEach(name => {
        newPathParams[name] = '';
      });
      setPathParams(newPathParams);

      // Reset query params and request body
      setQueryParams({});
      setRequestBody('');
      setShowRequestBody(['POST', 'PUT', 'PATCH'].includes(selectedMethod));
    } catch (err) {
      console.error('Error updating methods and parameters:', err);
    }
  }, [selectedEndpoint]);

  // Update query parameters when method changes
  useEffect(() => {
    if (!selectedEndpoint || !selectedMethod) return;

    try {
      const methodObj = apiSpec.paths[selectedEndpoint][selectedMethod.toLowerCase()];
      if (!methodObj) return;

      // Extract query parameters
      const parameters = methodObj.parameters || [];
      const queryParamObjs = parameters.filter(param => param.in === 'query');
      
      const newQueryParams = {};
      queryParamObjs.forEach(param => {
        newQueryParams[param.name] = '';
      });
      setQueryParams(newQueryParams);

      // Show request body for POST, PUT, PATCH
      setShowRequestBody(['POST', 'PUT', 'PATCH'].includes(selectedMethod));
      
      // Generate sample request body if needed
      if (['POST', 'PUT', 'PATCH'].includes(selectedMethod) && methodObj.requestBody) {
        const contentType = methodObj.requestBody.content['application/json'];
        if (contentType && contentType.schema) {
          if (contentType.schema.$ref) {
            // Handle schema reference
            const refPath = contentType.schema.$ref.split('/');
            const schemaName = refPath[refPath.length - 1];
            const schema = apiSpec.components.schemas[schemaName];
            
            if (schema) {
              const sampleObj = generateSampleFromSchema(schema);
              setRequestBody(JSON.stringify(sampleObj, null, 2));
            }
          } else {
            // Handle inline schema
            const sampleObj = generateSampleFromSchema(contentType.schema);
            setRequestBody(JSON.stringify(sampleObj, null, 2));
          }
        }
      } else {
        setRequestBody('');
      }
    } catch (err) {
      console.error('Error updating query parameters:', err);
    }
  }, [selectedEndpoint, selectedMethod]);

  // Generate a sample object from a schema
  const generateSampleFromSchema = (schema) => {
    if (!schema) return {};
    
    if (schema.type === 'object') {
      const result = {};
      if (schema.properties) {
        Object.keys(schema.properties).forEach(propName => {
          const propSchema = schema.properties[propName];
          result[propName] = generateSampleValue(propSchema);
        });
      }
      return result;
    }
    
    return {};
  };

  // Generate a sample value for a property
  const generateSampleValue = (propSchema) => {
    if (!propSchema) return null;
    
    if (propSchema.$ref) {
      const refPath = propSchema.$ref.split('/');
      const schemaName = refPath[refPath.length - 1];
      const schema = apiSpec.components?.schemas?.[schemaName];
      return schema ? generateSampleFromSchema(schema) : {};
    }
    
    switch (propSchema.type) {
      case 'string':
        return propSchema.example || 'string';
      case 'integer':
      case 'number':
        return propSchema.example || 0;
      case 'boolean':
        return propSchema.example || false;
      case 'array':
        return propSchema.example || [];
      case 'object':
        return generateSampleFromSchema(propSchema);
      default:
        if (propSchema.anyOf || propSchema.oneOf) {
          const options = propSchema.anyOf || propSchema.oneOf;
          if (options.length > 0) {
            return generateSampleValue(options[0]);
          }
        }
        return null;
    }
  };

  // Build the complete URL with path parameters
  const buildUrl = () => {
    let url = `${server}${selectedEndpoint}`;
    
    // Replace path parameters
    Object.entries(pathParams).forEach(([name, value]) => {
      url = url.replace(`{${name}}`, encodeURIComponent(value));
    });
    
    // Add query parameters
    const queryParamEntries = Object.entries(queryParams).filter(([_, value]) => value);
    if (queryParamEntries.length > 0) {
      const queryString = queryParamEntries
        .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
        .join('&');
      url += `?${queryString}`;
    }
    
    return url;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const url = buildUrl();
      const headers = {
        'Content-Type': 'application/json',
        'X-API-Tenant-Alias': tenantAlias,
        // Always include API key regardless of route type
        'X-API-Key': API_KEY
      };

      // Keep token handling for future use
      const token = localStorage.getItem('access_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Request Debug Info:', {
          url,
          method: selectedMethod,
          authScheme,
          headers: {
            ...headers,
            'X-API-Key': '[REDACTED]',
            'Authorization': headers['Authorization'] ? 'Bearer [REDACTED]' : 'Not Required'
          }
        });
      }

      let requestOptions = {
        method: selectedMethod,
        url,
        headers,
        withCredentials: true
      };
      
      // Add request body for POST, PUT, PATCH
      if (['POST', 'PUT', 'PATCH'].includes(selectedMethod) && requestBody) {
        try {
          requestOptions.data = JSON.parse(requestBody);
        } catch (err) {
          setError('Invalid JSON in request body');
          setLoading(false);
          return;
        }
      }
      
      const result = await axios(requestOptions);
      setResponse(result);
    } catch (err) {
      console.error('API request failed:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers
      });
      setError(err.response?.data?.message || err.message);
      if (err.response) {
        setResponse(err.response);
      }
    } finally {
      setLoading(false);
    }
  };

  // Add this useEffect near the top with other useEffect hooks
  useEffect(() => {
    // Validate API key on mount
    if (!API_KEY) {
      setError('API Key is not configured. Please check your environment variables.');
      return;
    }

    // Validate API key format


    // Log API key status in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Key validation passed');
    }
  }, []);

  // Add this effect to check token status
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    setHasToken(!!token);
  }, []);

  // Add this effect to determine auth scheme for selected endpoint
  useEffect(() => {
    if (!selectedEndpoint) return;
    
    // Determine auth scheme based on endpoint
    if (PUBLIC_ENDPOINTS.some(path => selectedEndpoint.startsWith(path))) {
      setAuthScheme('public');
    } else {
      setAuthScheme('api_key');
      // Note: 'full_auth' scheme reserved for future
    }
  }, [selectedEndpoint]);

  // Add a debug log to verify API key is loaded
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('API Key available:', !!API_KEY);
    }
  }, []);

  // Add new function to fetch OpenAPI JSON
  const fetchOpenApiJson = async () => {
    setOpenApiLoading(true);
    setOpenApiError(null);
    
    try {
      const response = await axios.get(`${server}/openapi.json`, {
        headers: {
          'X-API-Tenant-Alias': tenantAlias,
          'X-API-Key': API_KEY
        }
      });
      
      setOpenApiJson(response.data);
      
      // Create a download link
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = 'openapi.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error fetching OpenAPI JSON:', error);
      setOpenApiError(error.response?.data?.message || error.message);
    } finally {
      setOpenApiLoading(false);
    }
  };
  
  // Add new function to open Swagger UI
  const openSwaggerUI = () => {
    window.open(`${server}/docs`, '_blank');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>API Test Tool</Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Server Configuration */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Server"
                fullWidth
                value={server}
                onChange={(e) => setServer(e.target.value)}
                helperText="Server host and port"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="API Version"
                fullWidth
                value={apiVersion}
                onChange={(e) => setApiVersion(e.target.value)}
                helperText="API version path"
                disabled
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                label="Tenant Alias"
                fullWidth
                value={tenantAlias}
                onChange={(e) => setTenantAlias(e.target.value)}
                helperText="X-APP-TENANT-ALIAS header value"
              />
            </Grid>
            
            {/* Add OpenAPI buttons */}
            <Grid item xs={12}>
              <ButtonGroup variant="outlined" sx={{ mt: 1 }}>
                <Button 
                  startIcon={<DownloadIcon />}
                  onClick={fetchOpenApiJson}
                  disabled={openApiLoading}
                >
                  {openApiLoading ? 'Loading...' : 'Get OpenAPI JSON'}
                </Button>
                <Button 
                  startIcon={<DescriptionIcon />}
                  onClick={openSwaggerUI}
                >
                  Open API Documentation
                </Button>
              </ButtonGroup>
              {openApiError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {openApiError}
                </Alert>
              )}
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">API Key Status:</Typography>
                {API_KEY ? (
                  <Alert severity="success" sx={{ py: 0 }}>
                    Configured
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ py: 0 }}>
                    Not Configured
                  </Alert>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Auth Scheme:</Typography>
                {authScheme === 'public' ? (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Public
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ py: 0 }}>
                    API Key Required
                  </Alert>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Auth Token Status:</Typography>
                {hasToken ? (
                  <Alert severity="success" sx={{ py: 0 }}>
                    Present (Reserved for future use)
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Not Required
                  </Alert>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Route Type:</Typography>
                {authScheme === 'public' ? (
                  <Alert severity="info" sx={{ py: 0 }}>
                    Public (API Key Still Required)
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    Protected
                  </Alert>
                )}
              </Box>
            </Grid>
            
            {/* Route Selection */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Route</InputLabel>
                <Select
                  value={selectedRoute}
                  onChange={(e) => setSelectedRoute(e.target.value)}
                  label="Route"
                >
                  <MenuItem value="">Select a route</MenuItem>
                  {routes.map(route => (
                    <MenuItem key={route} value={route}>{route}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!selectedRoute}>
                <InputLabel>Endpoint</InputLabel>
                <Select
                  value={selectedEndpoint}
                  onChange={(e) => setSelectedEndpoint(e.target.value)}
                  label="Endpoint"
                >
                  {endpoints.map(endpoint => (
                    <MenuItem key={endpoint} value={endpoint}>{endpoint}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!selectedEndpoint}>
                <InputLabel>Method</InputLabel>
                <Select
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                  label="Method"
                >
                  {selectedEndpoint && apiSpec.paths[selectedEndpoint] ? 
                    Object.keys(apiSpec.paths[selectedEndpoint]).map(method => (
                      <MenuItem key={method} value={method.toUpperCase()}>
                        {method.toUpperCase()}
                      </MenuItem>
                    )) : 
                    <MenuItem value="GET">GET</MenuItem>
                  }
                </Select>
              </FormControl>
            </Grid>
            
            {/* Path Parameters */}
            {Object.keys(pathParams).length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Path Parameters</Typography>
                <Grid container spacing={2}>
                  {Object.entries(pathParams).map(([name, value]) => (
                    <Grid item xs={12} md={4} key={name}>
                      <TextField
                        label={name}
                        fullWidth
                        value={value}
                        onChange={(e) => setPathParams({...pathParams, [name]: e.target.value})}
                        required
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            
            {/* Query Parameters */}
            {Object.keys(queryParams).length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Query Parameters</Typography>
                <Grid container spacing={2}>
                  {Object.entries(queryParams).map(([name, value]) => (
                    <Grid item xs={12} md={4} key={name}>
                      <TextField
                        label={name}
                        fullWidth
                        value={value}
                        onChange={(e) => setQueryParams({...queryParams, [name]: e.target.value})}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            
            {/* Request Body */}
            {showRequestBody && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Request Body</Typography>
                <TextField
                  multiline
                  fullWidth
                  rows={10}
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
            )}
            
            {/* URL Preview */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Request URL</Typography>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper', 
                  border: '1px solid #ddd', 
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}
              >
                {buildUrl()}
              </Paper>
            </Grid>
            
            {/* Submit Button */}
            <Grid item xs={12}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={loading || !selectedEndpoint}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Request'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Response Display */}
      {response && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Response</Typography>
          
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity={response.status >= 200 && response.status < 300 ? "success" : "warning"}
              sx={{ mb: 2 }}
            >
              Status: {response.status} {response.statusText}
            </Alert>
            
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Response Headers</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd', 
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.8rem'
                  }}
                >
                  {JSON.stringify(response.headers, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
            
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Response Body</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd', 
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.8rem'
                  }}
                >
                  {JSON.stringify(response.data, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ApiTestPage; 